/**
 * InsightPro™ — Alert Preferences  v1.0
 * Per-user alert config: email, WhatsApp, push channels per alert type.
 * System-level SMTP/WhatChimp credentials stored in systemConfig (superAdmin only).
 */
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp, getFirestore, collection, getDocs, arrayUnion } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { useCompany } from '../context/CompanyContext';
import app from '../utils/firebase';

const db   = getFirestore();
const auth = getAuth();

// ── Helpers ──────────────────────────────────────────────────
const fmtR = n => n == null ? '' : `R ${Number(n).toLocaleString('en-ZA')}`;

function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'60px 0' }}>
      <div style={{ width:24, height:24, border:'3px solid rgba(196,146,42,0.2)', borderTop:'3px solid var(--gold)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Toggle component ──────────────────────────────────────────
function Toggle({ value, onChange, disabled }) {
  return (
    <div onClick={() => !disabled && onChange(!value)}
      style={{
        width: 36, height: 20,
        background: value ? 'var(--gold)' : 'rgba(255,255,255,0.15)',
        border: '1px solid ' + (value ? 'var(--gold)' : 'var(--card-border)'),
        borderRadius: 10, position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s', flexShrink: 0,
        opacity: disabled ? 0.4 : 1,
      }}>
      <div style={{
        position: 'absolute', width: 14, height: 14,
        background: 'white', borderRadius: '50%',
        top: 3, left: value ? 19 : 3,
        transition: 'left 0.2s',
      }}/>
    </div>
  );
}

// ── Channel checkbox ──────────────────────────────────────────
function ChannelBox({ icon, label, value, onChange, disabled }) {
  return (
    <div onClick={() => !disabled && onChange(!value)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
        padding: '8px 10px', borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
        border: `1px solid ${value && !disabled ? 'var(--gold)' : 'var(--card-border)'}`,
        background: value && !disabled ? 'rgba(196,146,42,0.08)' : 'transparent',
        opacity: disabled ? 0.35 : 1,
        transition: 'all 0.15s', minWidth: 56,
      }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '0.08em', color: value && !disabled ? 'var(--gold)' : 'var(--page-muted)', textTransform: 'uppercase' }}>{label}</span>
    </div>
  );
}

// ── Default prefs ─────────────────────────────────────────────
const DEFAULT_PREFS = {
  phoneNumber: '',
  email: '',
  companies: [],
  alerts: {
    morningBrief:    { enabled: false, time: '07:30',   email: true,  whatsapp: true,  push: true  },
    debtorThreshold: { enabled: false, thresholdR: 50000, email: true, whatsapp: false, push: true  },
    stockAlert:      { enabled: false, daysBelowReorder: 30, email: false, whatsapp: true, push: false },
    branchTarget:    { enabled: false, targetR: 500000, email: false, whatsapp: false, push: false },
    weeklySummary:   { enabled: false, dayOfWeek: 'Monday', time: '08:00', email: true, whatsapp: true, push: false },
  },
};

const ALERT_META = [
  {
    key: 'morningBrief',
    icon: '🌅',
    label: 'Morning Brief',
    desc: 'Daily summary of revenue, top branch, and active alerts',
    extra: (a, set) => (
      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>Send at</span>
        <select value={a.time} onChange={e => set({ ...a, time: e.target.value })}
          style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, padding:'4px 8px', color:'var(--page-text)', fontFamily:'var(--font-mono)', fontSize:11, cursor:'pointer' }}>
          {['06:00','06:30','07:00','07:30','08:00','08:30','09:00'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>SAST</span>
      </div>
    ),
  },
  {
    key: 'debtorThreshold',
    icon: '⚠️',
    label: 'Debtor Threshold',
    desc: 'Alert when a debtor\'s overdue balance exceeds your threshold',
    extra: (a, set) => (
      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>Threshold</span>
        <input type="number" value={a.thresholdR}
          onChange={e => set({ ...a, thresholdR: Number(e.target.value) })}
          style={{ width:100, background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, padding:'4px 8px', color:'var(--page-text)', fontFamily:'var(--font-mono)', fontSize:11 }}/>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>ZAR</span>
      </div>
    ),
  },
  {
    key: 'stockAlert',
    icon: '📦',
    label: 'Stock Alert',
    desc: 'Products below reorder level or out of stock',
    extra: (a, set) => (
      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>Alert when below</span>
        <input type="number" value={a.daysBelowReorder}
          onChange={e => set({ ...a, daysBelowReorder: Number(e.target.value) })}
          style={{ width:60, background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, padding:'4px 8px', color:'var(--page-text)', fontFamily:'var(--font-mono)', fontSize:11 }}/>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>days cover</span>
      </div>
    ),
  },
  {
    key: 'branchTarget',
    icon: '🎯',
    label: 'Branch Target Miss',
    desc: 'Alert when a branch falls below its monthly revenue target',
    extra: (a, set) => (
      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>Target</span>
        <input type="number" value={a.targetR}
          onChange={e => set({ ...a, targetR: Number(e.target.value) })}
          style={{ width:100, background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, padding:'4px 8px', color:'var(--page-text)', fontFamily:'var(--font-mono)', fontSize:11 }}/>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>ZAR/month</span>
      </div>
    ),
  },
  {
    key: 'weeklySummary',
    icon: '📊',
    label: 'Weekly Summary',
    desc: 'WTD vs last week — revenue, units, top performers',
    extra: (a, set) => (
      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>Every</span>
        <select value={a.dayOfWeek} onChange={e => set({ ...a, dayOfWeek: e.target.value })}
          style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, padding:'4px 8px', color:'var(--page-text)', fontFamily:'var(--font-mono)', fontSize:11, cursor:'pointer' }}>
          {['Monday','Tuesday','Wednesday','Thursday','Friday'].map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>at</span>
        <select value={a.time} onChange={e => set({ ...a, time: e.target.value })}
          style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, padding:'4px 8px', color:'var(--page-text)', fontFamily:'var(--font-mono)', fontSize:11, cursor:'pointer' }}>
          {['07:00','07:30','08:00','08:30','09:00'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
    ),
  },
];

// ── Main Component ────────────────────────────────────────────
export default function AlertPreferences() {
  const { selectedCompany } = useCompany();
  const [prefs,   setPrefs]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState('');
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
  const [pushToken, setPushToken] = useState(null);
  const [availableCompanies, setAvailableCompanies] = useState([]);

  const uid = auth.currentUser?.uid;

  // Check push support, listen for foreground messages, and auto-register token
  useEffect(() => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setPushSupported(supported);
    if (supported && pushPermission === 'granted') {
      try {
        const messaging = getMessaging(app);
        onMessage(messaging, (payload) => {
          const { title, body } = payload.notification || {};
          new Notification(title || 'InsightPro', { body, icon: '/logo192.png' });
        });
        // Auto-register FCM token if permission already granted
        if (uid && !pushToken) {
          (async () => {
            try {
              const swReg = await navigator.serviceWorker.ready;
              console.log('[Push] SW ready, scope:', swReg.scope);
              const token = await getToken(messaging, {
                vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
                serviceWorkerRegistration: swReg,
              });
              if (token) {
                console.log('[Push] Token registered:', token.slice(0, 20) + '...');
                await setDoc(doc(db, 'users', uid, 'alertPreferences', 'config'), {
                  fcmTokens: arrayUnion(token),
                  updatedAt: serverTimestamp(),
                }, { merge: true });
                setPushToken(token);
                console.log('[Push] Token saved to Firestore');
              } else {
                console.warn('[Push] getToken returned empty');
              }
            } catch (e) {
              console.error('[Push] Token registration failed:', e);
            }
          })();
        }
      } catch (_) { /* messaging not available */ }
    }
  }, [pushPermission, uid, pushToken]);

  // Load available companies from userMemberships
  useEffect(() => {
    if (!uid) return;
    getDocs(collection(db, 'userMemberships', uid, 'companies'))
      .then(snap => {
        const cos = snap.docs.map(d => ({ id: d.id, name: d.data().name || d.id }));
        setAvailableCompanies(cos);
      })
      .catch(() => {});
  }, [uid]);

  // Load prefs
  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    getDoc(doc(db, 'users', uid, 'alertPreferences', 'config'))
      .then(snap => {
        if (snap.exists()) {
          // Deep merge with defaults to handle new alert types added later
          const data = snap.data();
          const merged = { ...DEFAULT_PREFS, ...data,
            alerts: Object.fromEntries(
              Object.entries(DEFAULT_PREFS.alerts).map(([k, def]) => [k, { ...def, ...(data.alerts?.[k] || {}) }])
            )
          };
          setPrefs(merged);
        } else {
          setPrefs({ ...DEFAULT_PREFS, email: auth.currentUser?.email || '' });
        }
      })
      .catch(() => setPrefs({ ...DEFAULT_PREFS, email: auth.currentUser?.email || '' }))
      .finally(() => setLoading(false));
  }, [uid]);

  const setAlert = (key, val) => setPrefs(p => ({ ...p, alerts: { ...p.alerts, [key]: val } }));

  const save = async () => {
    if (!uid) return;
    setSaving(true); setSaved(false); setError('');
    try {
      await setDoc(doc(db, 'users', uid, 'alertPreferences', 'config'), {
        ...prefs,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const requestPush = async () => {
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== 'granted') {
        alert('Push permission denied. Please enable notifications in your browser settings.');
        return;
      }
      const messaging = getMessaging(app);
      const swReg = await navigator.serviceWorker.ready;
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });
      if (token && uid) {
        await setDoc(doc(db, 'users', uid, 'alertPreferences', 'config'), {
          fcmTokens: arrayUnion(token),
          updatedAt: serverTimestamp(),
        }, { merge: true });
        setPushToken(token);
      }
    } catch (e) {
      alert('Could not enable push: ' + e.message);
    }
  };

  const sendTest = async () => {
    if (!uid) return;
    setTesting(true); setTestResult('');
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(
        'https://africa-south1-insightprofinal-3c793.cloudfunctions.net/dispatchAlert',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({
            uid,
            alertKey: 'morningBrief',
            testMessage: `InsightPro™ test alert — ${new Date().toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg' })}`,
          }),
        }
      );
      const data = await res.json();
      if (data.success) setTestResult('✓ Test sent — check your email & WhatsApp');
      else setTestResult('Error: ' + (data.error || 'Unknown'));
    } catch (e) {
      setTestResult('Error: ' + e.message);
    } finally {
      setTesting(false);
      setTimeout(() => setTestResult(''), 6000);
    }
  };

  if (loading) return <Spinner />;
  if (!prefs)  return null;

  const anyEnabled = Object.values(prefs.alerts).some(a => a.enabled);

  return (
    <div style={{ animation: 'fadeUp 0.4s ease both', maxWidth: 720 }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--page-text)', marginBottom: 6 }}>
          Alert Preferences
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--page-muted)' }}>
          Choose which alerts you receive and how — email, WhatsApp, or push notification.
        </div>
      </div>

      {/* Contact Details */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 16 }}>
          Contact Details
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginBottom: 6, letterSpacing: '0.06em' }}>
              Email Address
            </label>
            <input
              type="email"
              value={prefs.email}
              onChange={e => setPrefs(p => ({ ...p, email: e.target.value }))}
              placeholder="you@example.com"
              style={{ width: '100%', background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '8px 12px', color: 'var(--page-text)', fontFamily: 'var(--font-body)', fontSize: 13, outline: 'none' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginBottom: 6, letterSpacing: '0.06em' }}>
              WhatsApp Number
            </label>
            <input
              type="tel"
              value={prefs.phoneNumber}
              onChange={e => setPrefs(p => ({ ...p, phoneNumber: e.target.value }))}
              placeholder="+27 82 000 0000"
              style={{ width: '100%', background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '8px 12px', color: 'var(--page-text)', fontFamily: 'var(--font-body)', fontSize: 13, outline: 'none' }}
            />
          </div>
        </div>

        {/* Push notification status */}
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', letterSpacing: '0.06em', marginBottom: 3 }}>Push Notifications</div>
            <div style={{ fontSize: 12, color: pushSupported ? 'var(--page-muted)' : 'var(--page-muted)' }}>
              {!pushSupported
                ? 'Not supported on this device/browser'
                : pushPermission === 'granted'
                  ? pushToken ? '✓ Enabled & registered' : '✓ Enabled on this device'
                  : pushPermission === 'denied'
                    ? '✗ Blocked — enable in browser settings'
                    : 'Not yet enabled on this device'}
            </div>
          </div>
          {pushSupported && pushPermission !== 'granted' && pushPermission !== 'denied' && (
            <button onClick={requestPush}
              style={{ padding: '7px 14px', background: 'var(--gold)', border: 'none', borderRadius: 3, color: '#000', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em', cursor: 'pointer', fontWeight: 600 }}>
              ENABLE PUSH
            </button>
          )}
        </div>
      </div>

      {/* Companies */}
      {availableCompanies.length > 0 && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '18px 20px', marginBottom: 16 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 14 }}>
            Receive Alerts For
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {availableCompanies.map(co => {
              const selected = (prefs.companies || []).includes(co.id);
              return (
                <div key={co.id}
                  onClick={() => {
                    const current = prefs.companies || [];
                    const next = selected ? current.filter(id => id !== co.id) : [...current, co.id];
                    setPrefs(p => ({ ...p, companies: next }));
                  }}
                  style={{
                    padding: '8px 16px', borderRadius: 4, cursor: 'pointer',
                    border: `1px solid ${selected ? 'var(--gold)' : 'var(--card-border)'}`,
                    background: selected ? 'rgba(196,146,42,0.08)' : 'transparent',
                    color: selected ? 'var(--gold)' : 'var(--page-muted)',
                    fontFamily: 'var(--font-mono)', fontSize: 11,
                    transition: 'all 0.15s',
                  }}>
                  {selected ? '✓ ' : ''}{co.name}
                </div>
              );
            })}
          </div>
          {(prefs.companies || []).length === 0 && (
            <div style={{ marginTop: 10, fontSize: 11, color: '#e05252', fontFamily: 'var(--font-mono)' }}>
              ⚠ Select at least one company to receive alerts
            </div>
          )}
        </div>
      )}

      {/* Alert Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {ALERT_META.map((meta) => {
          const a = prefs.alerts[meta.key];
          return (
            <div key={meta.key} style={{ background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '14px 16px' }}>
              {/* Top row: icon + label + toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15 }}>{meta.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: a.enabled ? 'var(--page-text)' : 'var(--page-muted)' }}>{meta.label}</span>
                </div>
                <Toggle value={a.enabled} onChange={v => setAlert(meta.key, { ...a, enabled: v })} />
              </div>
              {/* Description */}
              <div style={{ fontSize: 11.5, color: 'var(--page-muted)', marginBottom: a.enabled ? 10 : 0 }}>{meta.desc}</div>
              {/* Channels row — only when enabled */}
              {a.enabled && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <ChannelBox icon="✉️" label="Email" value={a.email} disabled={!prefs.email}
                    onChange={v => setAlert(meta.key, { ...a, email: v })} />
                  <ChannelBox icon="💬" label="WA" value={a.whatsapp} disabled={!prefs.phoneNumber}
                    onChange={v => setAlert(meta.key, { ...a, whatsapp: v })} />
                  <ChannelBox icon="🔔" label="Push" value={a.push}
                    disabled={!pushSupported}
                    onChange={async v => {
                      if (pushPermission !== 'granted') {
                        await requestPush();
                        return;
                      }
                      setAlert(meta.key, { ...a, push: v });
                    }} />
                </div>
              )}
              {/* Extra config */}
              {a.enabled && meta.extra && (
                <div style={{ marginTop: 8 }}>
                  {meta.extra(a, val => setAlert(meta.key, val))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Validation hints */}
      {anyEnabled && !prefs.email && (
        <div style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          ⚠️ Add an email address to receive email alerts
        </div>
      )}
      {anyEnabled && !prefs.phoneNumber && (
        <div style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          ⚠️ Add a WhatsApp number to receive WhatsApp alerts
        </div>
      )}

      {/* Save */}
      <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
        <button onClick={save} disabled={saving}
          style={{ flex: '1 1 140px', padding: '12px 20px', background: 'var(--gold)', border: 'none', borderRadius: 3, color: '#000', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Preferences'}
        </button>
        <button onClick={sendTest} disabled={testing || !anyEnabled}
          style={{ flex: '1 1 120px', padding: '12px 20px', background: 'transparent', border: '1px solid var(--card-border)', borderRadius: 3, color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: (testing || !anyEnabled) ? 'not-allowed' : 'pointer', opacity: (testing || !anyEnabled) ? 0.5 : 1 }}>
          {testing ? 'Sending…' : 'Send Test'}
        </button>
      </div>
      {(saved || testResult || error) && (
        <div style={{ marginTop: 8 }}>
          {saved      && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--green)' }}>✓ Saved</span>}
          {testResult && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: testResult.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{testResult}</span>}
          {error      && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)' }}>{error}</span>}
        </div>
      )}

    </div>
  );
}
